import { Card, Col, Layout, Row } from "antd";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "../../../styles/Dashboard.css";
import { formatCurrency } from "./modules/formatCurrency";
import { getPercentChange } from "./modules/getPercentChange";
import { useBookings } from "./modules/useBookings";
import { useChart } from "./modules/useChart";
import { useDashboard } from "./modules/useDashboard";
import { ArrivalList } from "./molecules/arrivalList";
import { AvailabilityAlertToday } from "./molecules/availabilityAlertToday";
import { BookingPickup } from "./molecules/bookingPickup";
import { RoomNightPickup } from "./molecules/roomNightPickup";

const { Content } = Layout;

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels
);

function DashboardScreen() {
  const {
    todayBookingCount,
    todayCancelledBookings,
    todayADR,
    arrivalList,
    overbookedRooms,
    todayStayADR,
    todayTotalDays,
    todayStayRoomNight,
    todayRevenue,
    todayStayRevenue,
  } = useDashboard();
  const { chartData, monthYear, roomNightChartData } = useChart();
  const { lastWeekDataReport, nextWeekDataReport } = useBookings();

  return (
    <Layout style={{ minHeight: "100vh", width: "100vw" }}>
      <Content style={{ padding: "20px", width: "100%", margin: 0 }}>
        <h1 style={{ textAlign: "Left" }}>Admin Dashboard</h1>

        {/* Use full-width rows (no centering) so cards span the available screen */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              title="New Bookings Today"
              bordered
              style={{ textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "1000",
                  color: "#433d80",
                }}
              >
                {todayBookingCount}
              </p>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              title="Today's Booked Room Night"
              bordered
              style={{ textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "1000",
                  color: "#433d80",
                }}
              >
                {todayTotalDays}
              </p>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              title="Today's Booked Revenue"
              bordered
              style={{ textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: "#008000",
                }}
              >
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "VND",
                }).format(todayRevenue)}
              </p>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              title="Today's Booked ADR (Average Daily Rate)"
              bordered
              style={{ textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: "#1e90ff",
                }}
              >
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "VND",
                }).format(todayADR)}
              </p>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              title="Today's Cancelled Bookings"
              bordered
              style={{ textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "1000",
                  color: "#ff0000",
                }}
              >
                {todayCancelledBookings}
              </p>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              title="Stay Room Night Today"
              bordered
              style={{ textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "1000",
                  color: "#433d80",
                }}
              >
                {todayStayRoomNight}
              </p>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              title="Today Stay Revenue"
              bordered
              style={{ textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: "#008000",
                }}
              >
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "VND",
                }).format(todayStayRevenue)}
              </p>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              title="Today's Stay ADR (Average Daily Rate)"
              bordered
              style={{ textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: "#1e90ff",
                }}
              >
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "VND",
                }).format(todayStayADR)}
              </p>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Card
              title="Pace Summary"
              extra={
                <a href="/admin/analytics" className="navigation-link">
                  Pace report →
                </a>
              }
              style={{ minHeight: "400px" }}
            >
              <Row gutter={[32, 32]}>
                <Col span={12}>
                  <h4>LAST 7 DAYS</h4>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <div className="item-wrapper">
                      <div style={{ color: "#666" }}>Room nights sold</div>
                      <div className="info">
                        {lastWeekDataReport.totalRoomNightsBooked}
                        {getPercentChange(
                          lastWeekDataReport.totalRoomNightsBooked,
                          lastWeekDataReport.totalRoomNightsBookedPreviousYear
                        )}
                      </div>
                    </div>

                    <div className="item-wrapper">
                      <div className="item-title">Revenue (VND)</div>
                      <div className="info">
                        {formatCurrency(lastWeekDataReport.totalRevenueBooked)}
                        {getPercentChange(
                          lastWeekDataReport.totalRevenueBooked,
                          lastWeekDataReport.totalRevenueBookedPreviousYear
                        )}
                      </div>
                    </div>

                    <div className="item-wrapper">
                      <div className="item-title">ADR (VND)</div>
                      <div className="info">
                        {formatCurrency(lastWeekDataReport.totalADRBooked)}
                        {getPercentChange(
                          lastWeekDataReport.totalADRBooked,
                          lastWeekDataReport.totalADRBookedPreviousYear
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <h4>NEXT 7 DAYS</h4>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <div className="item-wrapper">
                      <div className="item-title">Room nights sold</div>
                      <div className="info">
                        {nextWeekDataReport.totalRoomNightsBooked}
                        {getPercentChange(
                          nextWeekDataReport.totalRoomNightsBooked,
                          nextWeekDataReport.totalRoomNightsBookedPreviousYear
                        )}
                      </div>
                    </div>

                    <div className="item-wrapper">
                      <div className="item-title">Revenue (VND)</div>
                      <div className="info">
                        {formatCurrency(nextWeekDataReport.totalRevenueBooked)}
                        {getPercentChange(
                          nextWeekDataReport.totalRevenueBooked,
                          nextWeekDataReport.totalRevenueBookedPreviousYear
                        )}
                      </div>
                    </div>

                    <div className="item-wrapper">
                      <div className="item-title">ADR (VND)</div>
                      <div className="info">
                        {formatCurrency(nextWeekDataReport.totalADRBooked)}
                        {getPercentChange(
                          nextWeekDataReport.totalADRBooked,
                          nextWeekDataReport.totalADRBookedPreviousYear
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Card
              title="Today's Arrival List"
              bordered
              style={{ minHeight: "400px" }}
            >
              {arrivalList.length > 0 ? (
                <ArrivalList arrivalList={arrivalList} />
              ) : (
                <p>No arrivals scheduled for today.</p>
              )}
            </Card>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <AvailabilityAlertToday overbookedRooms={overbookedRooms} />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <BookingPickup chartData={chartData} monthYear={monthYear} />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <RoomNightPickup
              roomNightChartData={roomNightChartData}
              monthYear={monthYear}
            />
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}

export default DashboardScreen;
